import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import {
  ConfirmDialogComponent,
  ConfirmDialogData
} from '@app/shared/components';
import { Utils } from '@app/shared/helpers/utils';
import { companyValidationMessages } from '@app/shared/validation.message';
import { merge, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import {
  CustomValidators,
  DomainService,
  EntityService,
  ErroMessages,
  FormServiceValidationMessages,
  IEntity,
  IErroMessages,
  Pattern,
  TokenService,
  ViggoCoreCustomInputComponent
} from 'viggo-sistemas-core-web';
import {
  validateCnpj,
  validateCpf
} from '../../../../shared/utils/validators_cpf_cnpj';
import { IParceiro } from '../../model/parceiro.model';
import { ParceiroService } from '../../services/parceiro.service';
import { DialogConsultaPredictusComponent } from '../dialog-consulta-predictus/dialog-consulta-predictus.component';

@Component({
  selector: 'parceiro-form-company',
  templateUrl: './form-company.component.html',
  styleUrls: ['./form-company.component.scss'],
  providers: [ParceiroService]
})
export class FormCompanyComponent<T extends IEntity>
  implements OnInit, AfterViewInit {
  @ViewChildren(ViggoCoreCustomInputComponent)
  elementsInputs: QueryList<ViggoCoreCustomInputComponent>;

  messageService: FormServiceValidationMessages;
  form: UntypedFormGroup;
  displayMessage = {};
  findParceiro = false;
  loadingParceiro = false;

  @Input() entity: any;
  @Input() notRequired = false;
  @Input() requiredFantasia = false;
  @Input() showObs = false;
  @Input() usaLimiteOuSaldo = false;
  @Input() parceiroType = 'parceiro' as any;

  @Output() onCancelClick = new EventEmitter();

  @Output() onLoadParceiro = new EventEmitter<IParceiro>();

  @Output() onClearIdFromEntityParceiro = new EventEmitter<void>();

  @Output() isRequired = new EventEmitter<boolean>();

  @Input() entityService: EntityService<T>;

  focusElement: any;
  inputElementDtNasc: boolean;
  ID_FIELD_DTNASC = 'field-data-nascimento';
  ID_DTPICKER_NASC = 'dtPickerToggleDtNasc';

  constructor(
    public matSnackBar: MatSnackBar,
    public matDialog: MatDialog,
    protected router: Router,
    protected formBuilder: UntypedFormBuilder,
    protected tokenService: TokenService,
    protected parceiroService: ParceiroService,
    protected domainService: DomainService,
  ) {
    this.messageService = new FormServiceValidationMessages(
      companyValidationMessages
    );
  }
  ngOnInit(): void {
    const today = new Date();
    const { entity } = this;
    this.form = this.formBuilder.group(
      this.notRequired &&
        (this.parceiroType === 'cliente' || this.parceiroType === 'deposito')
        ? {
            codigo: [entity.codigo],
            nome_razao_social: [
              entity.parceiro.nome_razao_social,
              [
                Validators.required,
                Validators.maxLength(60),
                Validators.minLength(1),
                CustomValidators.isEmptyValue()
              ]
            ],
            apelido_nome_fantasia: [
              entity.parceiro.apelido_nome_fantasia,
              this.requiredFantasia
                ? [
                    Validators.required,
                    Validators.maxLength(60),
                    Validators.minLength(1),
                    CustomValidators.isEmptyValue()
                  ]
                : [
                    Validators.maxLength(60),
                    Validators.minLength(1),
                    CustomValidators.isEmptyValue()
                  ]
            ],
            cpf_cnpj: [entity.parceiro.cpf_cnpj],
            rg_insc_est: [
              entity.parceiro.rg_insc_est,
              [
                Validators.pattern(Pattern.patternInscEstadual()),
                Validators.maxLength(20),
                CustomValidators.existSpecialChar(),
                CustomValidators.isEmptyValue()
              ]
            ],
            data_nascimento: [
              entity.parceiro.data_nascimento,
              [
                CustomValidators.date(entity.parceiro.data_nascimento, today),
                CustomValidators.isEmptyValue()
              ]
            ],
            observacao: [
              entity.observacao,
              [Validators.maxLength(500), CustomValidators.isEmptyValue()]
            ],
            limite_credito: [
              entity.limite_credito
                ? Utils.replaceDotToComma(entity.limite_credito, 2)
                : 0
            ]
          }
        : {
            codigo: [entity.codigo],
            nome_razao_social: [
              entity.parceiro.nome_razao_social,
              [
                Validators.required,
                Validators.maxLength(60),
                Validators.minLength(1),
                CustomValidators.isEmptyValue()
              ]
            ],
            apelido_nome_fantasia: [
              entity.parceiro.apelido_nome_fantasia,
              this.requiredFantasia
                ? [
                    Validators.required,
                    Validators.maxLength(60),
                    Validators.minLength(1),
                    CustomValidators.isEmptyValue()
                  ]
                : [
                    Validators.maxLength(60),
                    Validators.minLength(1),
                    CustomValidators.isEmptyValue()
                  ]
            ],
            cpf_cnpj: [entity.parceiro.cpf_cnpj, [Validators.required]],
            rg_insc_est: [
              entity.parceiro.rg_insc_est,
              [
                Validators.pattern(Pattern.patternInscEstadual()),
                Validators.maxLength(20),
                CustomValidators.existSpecialChar(),
                CustomValidators.isEmptyValue()
              ]
            ],
            data_nascimento: [
              entity.parceiro.data_nascimento,
              [
                CustomValidators.date(entity.parceiro.data_nascimento, today),
                CustomValidators.isEmptyValue()
              ]
            ],
            limite_credito: [entity.limite_credito ? entity.limite_credito : 0]
          }
    );

    this.nextStepEmpresaForm();

    const {
      cpf_cnpj,
      apelido_nome_fantasia,
      nome_razao_social,
      rg_insc_est
    } = this.form.controls;

    if (this.entity.id) {
      cpf_cnpj.disable({ emitEvent: false });
      this.isRequired.emit(false);
      this.notRequired = false;
    }

    cpf_cnpj.valueChanges.subscribe(vl => {
      if (!vl || vl === '') {
        if (
          !this.notRequired &&
          (this.parceiroType === 'cliente' || this.parceiroType === 'deposito')
        ) {
          nome_razao_social.setValue(apelido_nome_fantasia.value);
          apelido_nome_fantasia.reset();
        }
        rg_insc_est.reset();
        this.isRequired.emit(true);
        this.notRequired = true;
        this.nextStepEmpresaForm();
      } else {
        if (
          this.notRequired &&
          (this.parceiroType === 'cliente' || this.parceiroType === 'deposito')
        ) {
          apelido_nome_fantasia.setValue(nome_razao_social.value);
          // nome_razao_social.reset();
        }
        this.isRequired.emit(false);
        this.notRequired = false;
        this.removeValidatorMask();
        this.validar();
        this.nextStepEmpresaForm();
      }
    });
  }

  notText(event) {
    if (
      (!/^\d+$/.test(event.key) || /[`,.]/.test(event.key)) &&
      event.key !== 'Backspace' &&
      event.key !== 'Delete' &&
      event.key !== 'Tab' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight' &&
      !event.ctrlKey
    )
      return false;
  }

  removeValidatorMask() {
    const { cpf_cnpj } = this.form.controls;
    if (cpf_cnpj.hasError('mask')) cpf_cnpj.setErrors(null);
  }

  validar() {
    const cpfCnpjValue = this.form.controls.cpf_cnpj.value;
    if (
      cpfCnpjValue.toString().length <= 11 &&
      this.parceiroType !== 'deposito'
    ) {
      if (validateCpf(cpfCnpjValue)) {
        this.form.controls.cpf_cnpj.setErrors({ validateCPF: true });
        this.form.updateValueAndValidity();
      }
    } else {
      if (validateCnpj(cpfCnpjValue)) {
        this.form.controls.cpf_cnpj.setErrors({ validateCNPJ: true });
        this.form.updateValueAndValidity();
      }
    }
  }

  /**
   * Abre um dialog que contém a consulta na predictus
   */
  openDialogConsultaPredictus() {
    this.loadingParceiro = true;
    const cpfCnpjValue: string = this.form.controls.cpf_cnpj.value.replace(
      /\D/g,
      ''
    );

    this.domainService.consultaPredictus(cpfCnpjValue).subscribe(
      (response: any) => {
        response.contatos = [];
        this.loadingParceiro = false;
        this.matDialog
          .open(DialogConsultaPredictusComponent, {
            data: { data: response, usaCnae: false },
            panelClass: ['transparent-dialog']
          })
          .afterClosed()
          .subscribe((res: IParceiro) => {
            if (res) {
              this.onLoadParceiro.emit({
                ...res,
                id: this.entity?.id
              });
              this.form.patchValue({
                apelido_nome_fantasia: res.apelido_nome_fantasia || null,
                nome_razao_social: res.nome_razao_social || null,
                rg_insc_est: res.rg_insc_est || null,
                data_nascimento: res.data_nascimento || null
              });
            }
          });
      },
      (err: HttpErrorResponse) => {
        this.loadingParceiro = false;
        this.displayError(err, false);
      }
    );
  }

  public checkCpfCnpj() {
    const cpfCnpj = this.form.controls.cpf_cnpj;

    if (
      !cpfCnpj.value ||
      cpfCnpj.value === undefined ||
      cpfCnpj.value.length < 11 ||
      cpfCnpj.hasError('validateCPF') ||
      cpfCnpj.hasError('validateCNPJ')
    )
      return;
    const cpfCnpjValue: string = cpfCnpj.value.replace(/\D/g, '');
    this.loadingParceiro = true;

    this.parceiroService
      .getEntitiesByParams(
        `cpf_cnpj=${cpfCnpjValue}`,
        this.tokenService.getDomain().id
      )
      .pipe(
        map(parceiros => {
          if (parceiros.length) {
            this.findParceiro = true;
            return { ...parceiros[0], cpf_cnpj: parceiros[0].cpf_cnpj.trim() };
          } else {
            this.findParceiro = false;
            ({ contatos: [], enderecos: [] } as IParceiro);
          }
        }),
        mergeMap(parceiro => {
          if (!parceiro || !parceiro.id || parceiro.id === this.entity.id)
            return of(parceiro);
          return this.entityService.getEntity(parceiro.id).pipe(
            map(ent => {
              if (ent && ent.id) {
                this.form.controls.cpf_cnpj.setErrors({ duplicate: true });
                this.form.updateValueAndValidity();
                return { contatos: [], enderecos: [] } as IParceiro;
              }
              return parceiro;
            }),
            catchError(() => of(parceiro))
          );
        })
      )
      .subscribe(
        (parceiro: IParceiro) => {
          this.loadingParceiro = false;
          if (!parceiro || !parceiro.id) {
            this.form.controls.rg_insc_est.setValue('');
            this.onClearIdFromEntityParceiro.emit();
            return;
          } else {
            this.onLoadParceiro.emit(parceiro);
            this.form.patchValue({
              apelido_nome_fantasia: parceiro.apelido_nome_fantasia || null,
              nome_razao_social: parceiro.nome_razao_social || null,
              rg_insc_est: parceiro.rg_insc_est || null,
              // user: parceiro.user || null,
              // user_id: parceiro.user_id || null,
              data_nascimento: parceiro.data_nascimento || null
            });
          }
        },
        (err: HttpErrorResponse) => {
          this.loadingParceiro = false;
          this.displayError(err);
        }
      );
  }

  public checkRgInscEstadual(): void {
    const rgInsc = this.form.controls.rg_insc_est;

    if (
      rgInsc.invalid ||
      rgInsc.value === null ||
      this.findParceiro ||
      rgInsc.value === '' ||
      rgInsc.value === this.entity.parceiro.rg_insc_est
    )
      return;
    const rgInscValue: string = rgInsc.value.replace(/[^a-zA-Z0-9 ]/g, '');

    this.parceiroService
      .getEntitiesByParams(
        `rg_insc_est=${rgInscValue}`,
        this.tokenService.getDomain().id
      )
      .subscribe(
        (parceiro: IParceiro) => {
          if (parceiro[0] && parceiro[0].id) {
            this.form.controls.rg_insc_est.setErrors({ duplicate: true });
            this.form.updateValueAndValidity();
            return {} as IParceiro;
          }
        },
        (err: HttpErrorResponse) => {
          this.displayError(err);
        }
      );
  }

  getValues(): IParceiro {
    const {
      nome_razao_social,
      apelido_nome_fantasia,
      cpf_cnpj,
      rg_insc_est,
      data_nascimento
    } = this.form.value;
    return Utils.trimAttributes({
      nome_razao_social: nome_razao_social,
      apelido_nome_fantasia: apelido_nome_fantasia,
      cpf_cnpj: cpf_cnpj,
      rg_insc_est: rg_insc_est,
      data_nascimento: data_nascimento
    } as IParceiro);
  }

  getObservacaoValue(): string {
    return this.form.value.observacao
      ? this.form.value.observacao.trim()
      : this.form.value.observacao;
  }

  ngAfterViewInit() {
    this.initSubscribFormChanges();
  }

  initSubscribFormChanges() {
    const controlBlurs = this.elementsInputs.map(el => el.blur);

    merge(this.form.valueChanges, ...controlBlurs).subscribe(() => {
      this.displayMessage = this.messageService.getMessagesForm(this.form);
    });
  }

  nextStepEmpresaForm() {
    this.form.markAllAsTouched();
    this.markAllControlsAsDirty();
    if (!this.form.valid) {
      this.displayMessage = this.messageService.getMessagesForm(this.form);
    }
  }

  markAllControlsAsDirty() {
    Object.keys(this.form.controls).forEach(controlKey => {
      controlKey = String(controlKey);
      const control = this.form.get(controlKey);
      if (!control || control.valid) return;
      control.markAsDirty();
    });
  }

  onFocus(event, input?: boolean) {
    if (input !== undefined && input !== null)
      if (document.activeElement.id === this.ID_FIELD_DTNASC) {
        this.inputElementDtNasc =
          event?.target?.id === document.activeElement.id;
      } else {
        this.inputElementDtNasc = false;
      }
    if (event) {
      this.focusElement = event.target?.id;
    } else {
      this.focusElement = null;
    }
  }

  displayError(err: HttpErrorResponse, showDefaultMsg = true) {
    const errorMsg = new ErroMessages({
      route: this.router.url,
      router: this.router,
      matSnackBar: this.matSnackBar,
      httpError: err,
      showDefaultMsg: showDefaultMsg,
      loginService: null,
      tokenService: this.tokenService,
      dialogComponent: ConfirmDialogComponent,
      matDialog: this.matDialog,
      dialogData: {
        config: {
          data: {
            title: 'Aviso - Erro ao realizar operação',
            bodyMessage: err.error,
            icon: 'warning',
            icon_color: 'accent',
            singleBtn: true
          } as ConfirmDialogData,
          disableClose: true,
          width: '500px'
        } as MatDialogConfig<any>
      }
    } as IErroMessages);
    errorMsg.checkError();
  }
}
